import type { MediaPermissionState } from '../models/device.model'

export function mapMediaErrorToPermissionState(error: unknown): MediaPermissionState {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError') return 'denied'
    if (error.name === 'NotFoundError') return 'error'
  }
  return 'error'
}

export type BrowserInfo = {
  name: string
  isSafari: boolean
  isFirefox: boolean
  isChrome: boolean
}

export function detectBrowser(): BrowserInfo {
  const ua = navigator.userAgent.toLowerCase()
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  const isFirefox = ua.includes('firefox')
  const isChrome = ua.includes('chrome') && !isSafari
  return {
    name: isChrome ? 'Chrome' : isSafari ? 'Safari' : isFirefox ? 'Firefox' : 'Other',
    isSafari,
    isFirefox,
    isChrome,
  }
}

type WebkitWindow = Window & { webkitAudioContext?: typeof AudioContext }

export function getAudioContextCtor(): typeof AudioContext | undefined {
  return window.AudioContext ?? (window as unknown as WebkitWindow).webkitAudioContext
}

/**
 * True only if BOTH camera and microphone permissions are already `granted`.
 *
 * Used to skip the "ready to join" permission prompt when the user has already
 * granted access. The Permissions API's camera/microphone descriptors aren't
 * universally supported (Firefox lacks them; Safari is partial) — whenever we
 * can't positively confirm both are granted we return `false` so the caller
 * still shows the prompt (safe default, never hides a needed request).
 */
export async function queryMediaPermissionsGranted(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return false
  try {
    const [cam, mic] = await Promise.all([
      navigator.permissions.query({ name: 'camera' as PermissionName }),
      navigator.permissions.query({ name: 'microphone' as PermissionName }),
    ])
    return cam.state === 'granted' && mic.state === 'granted'
  } catch {
    return false
  }
}

/**
 * Per-device permission state, without opening the device.
 *
 * The dialog needs to know whether the browser has already refused so it can
 * explain how to unblock it. That used to be answered by opening a full
 * camera-and-microphone stream and immediately discarding it, which is an
 * expensive way to ask a question the Permissions API answers directly.
 *
 * Where the descriptors are unsupported — Firefox has neither; Safari is
 * partial — both come back `'prompt'`, which is the honest answer: we do not
 * know. The caller shows nothing rather than guessing, and the real capture
 * still reports the truth when it happens.
 */
export async function readPermissionStates(): Promise<{
  camera: MediaPermissionState
  microphone: MediaPermissionState
}> {
  const unknown = { camera: 'prompt' as const, microphone: 'prompt' as const }
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return unknown
  const read = async (name: 'camera' | 'microphone'): Promise<MediaPermissionState> => {
    try {
      const status = await navigator.permissions.query({ name: name as PermissionName })
      if (status.state === 'granted') return 'granted'
      if (status.state === 'denied') return 'denied'
      return 'prompt'
    } catch {
      return 'prompt'
    }
  }
  const [camera, microphone] = await Promise.all([read('camera'), read('microphone')])
  return { camera, microphone }
}
